import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef, useState } from "react";
import { z as zod } from "zod";
import { DialogWrapper } from "../../pages/SharedPages/OrganizationPage/ProductsList/BulkActionOnProducts/utils";
import {
  ConfirmOrCancelButtonContainer,
  DeleteButton,
  EditButton,
  TertiaryButtonSmall,
  TertiaryTextButtonSmall,
} from "../Buttons/Buttons";
import { GenericDialogBody } from "../ConfirmDialog/ConfirmDialog";
import {
  BookmarkIcon,
  BookmarkPlusIcon,
  CaretDownIcon,
  CheckIcon,
} from "../Icons/Icons";
import { Row } from "../Layout/Layout";
import { TextField } from "../TextFields/TextFields";
import { H3 } from "../Typography/Typography";
import { zodRequiredString } from "../../util/zod.util";
import { useFormWrapper } from "../../util/util";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import type { IPresetDataItem, IPresetFilter } from "../../types/types";

interface DropDownContainerTypes {
  dropdownWidth?: string;
}

const DropDownContainer = styled.div<DropDownContainerTypes>`
  box-shadow: 0px 6px 16px -6px ${({ theme }) => theme.shadowColor};
  border: solid 1px ${({ theme }) => theme.secondaryBorder};
  border-radius: 5px;
  position: absolute;
  top: 35px;
  z-index: 3;
  width: ${({ dropdownWidth }) => dropdownWidth || "400px"};
  overflow: auto;
  max-height: 182px;
  button {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background-color: ${({ theme }) => theme.primaryBG};
    border: none;
    width: 100%;
    white-space: nowrap;
    padding: 12px 4px 12px 12px;
    cursor: pointer;
    font-size: ${({ theme }) => theme.fontSizes.small};
    font-weight: ${({ theme }) => theme.fontSizes.small};
    color: ${({ theme }) => theme.primaryTextColor};
    height: 45px;
  }

  button:focus {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    outline: none;
    color: ${({ theme }) => theme.primaryTextColor};
  }

  button:hover {
    background-color: ${({ theme }) => theme.secondaryButtonBG};
    color: ${({ theme }) => theme.primaryTextColor};
  }

  .icon-container {
    display: none;
  }

  .icon-checked {
    display: flex;
    margin-right: 10px;
  }

  button:hover .icon-container {
    display: flex;
    align-items: center;
  }

  button:hover .icon-checked {
    display: none;
  }

  button > span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex-grow: 1;
    padding-right: 10px;
    text-align: left;
  }
`;

export const PresetFilter = ({
  dropdownWidth,
  presetFilterData,
  isUpdatePreset,
  selectedPreset,
  showPresetDialog,
  setShowPresetDialog,
  setIsEditMode,
  onDeletePreset,
  onApplyPreset,
  onUpdatePreset,
  onSubmitPreset,
}: {
  dropdownWidth?: string;
  presetFilterData: IPresetFilter | undefined;
  isUpdatePreset: boolean;
  selectedPreset: IPresetDataItem | undefined;
  showPresetDialog: boolean;
  setShowPresetDialog: Dispatch<SetStateAction<boolean>>;
  setIsEditMode: Dispatch<SetStateAction<boolean>>;
  onDeletePreset: (id: string) => Promise<void>;
  onApplyPreset: (
    id: string,
    setShowDropdown: Dispatch<SetStateAction<boolean>>
  ) => Promise<void>;
  onUpdatePreset: () => Promise<void>;
  onSubmitPreset: (preset_name: string) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const displayName = presetFilterData?.data?.[0]?.display_name || "";
  const hasPresetClear =
    !!presetFilterData?.data?.length || selectedPreset?.selected;

  const validationSchema = zod.object({
    preset_name: zodRequiredString(t),
  });
  type FormInputs = zod.infer<typeof validationSchema>;

  const { handleSubmit, register, reset, formState, errors } =
    useFormWrapper<FormInputs>({
      defaultValues: {
        preset_name: displayName,
      },
      resolver: zodResolver(validationSchema),
    });

  useEffect(() => {
    reset({ preset_name: displayName });
  }, [displayName, reset]);

  const toggleDialog = () => setShowPresetDialog((prev) => !prev);
  const toggleDropdown = () => setShowDropdown((prev) => !prev);

  const handleEdit = () => {
    setIsEditMode(true);
    setShowPresetDialog(true);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <TertiaryButtonSmall
        active={false}
        onClick={displayName ? toggleDropdown : toggleDialog}
        style={{ padding: "7px 12px" }}
      >
        <Row style={{ gap: "6px" }}>
          {displayName ? (
            <BookmarkIcon width={16} height={16} />
          ) : (
            <BookmarkPlusIcon width={16} height={16} />
          )}
          <span>
            {!!selectedPreset
              ? displayName
              : hasPresetClear
              ? t("Preset")
              : displayName || t("Save Preset")}
          </span>
          {displayName && <CaretDownIcon width={16} height={16} />}
        </Row>
      </TertiaryButtonSmall>
      {!isUpdatePreset && !!selectedPreset && (
        <TertiaryTextButtonSmall
          onClick={onUpdatePreset}
          style={{ paddingBottom: "8px" }}
        >
          {t("Update Preset")}
        </TertiaryTextButtonSmall>
      )}
      <GenericDialogBody
        show={showPresetDialog}
        closeDialog={() => setShowPresetDialog(false)}
      >
        <DialogWrapper style={{ gap: "24px" }}>
          <H3 style={{ margin: 0 }}>{t("Save Preset")}</H3>
          <TextField
            name="preset_name"
            label={t("Preset Name")}
            theref={register({
              required: true,
            })}
            formState={formState}
            errors={errors}
            type="text"
          />
          <ConfirmOrCancelButtonContainer
            confirmText={t("Save")}
            isConfirmLoading={false}
            onConfirm={handleSubmit((data) => onSubmitPreset(data.preset_name))}
            onCancel={toggleDialog}
          />
        </DialogWrapper>
      </GenericDialogBody>
      {showDropdown && (
        <DropDownContainer ref={dropdownRef} dropdownWidth={dropdownWidth}>
          {presetFilterData?.data?.map((item) => {
            return (
              <button
                key={item.id}
                onClick={() => onApplyPreset(item.id, setShowDropdown)}
              >
                <span>{item.display_name}</span>
                <div className="icon-container">
                  <DeleteButton
                    width={20}
                    height={20}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeletePreset(item.id);
                    }}
                    style={{
                      backgroundColor: theme.secondaryButtonBG,
                      padding: "12px 8px",
                    }}
                  />
                  <EditButton
                    width={20}
                    height={20}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit();
                    }}
                    style={{
                      backgroundColor: theme.secondaryButtonBG,
                      padding: "12px 8px",
                    }}
                  />
                </div>
                {!!selectedPreset && (
                  <div className="icon-checked">
                    <CheckIcon fill={theme.primaryButtonBorder} />
                  </div>
                )}
              </button>
            );
          })}
        </DropDownContainer>
      )}
    </>
  );
};
